import { HttpClient } from '../http-client';
import type { BaseResponse } from '../types/common';
import type {
  ProjectImportRequest,
  ProjectRegisterRequest,
  ProjectRegisterResponse,
  ProjectResponse,
  ProjectSearchRequest,
  ProjectSearchResponse,
  ProjectUpdateRequest
} from '../types/project.types';

function normalizePageable(params: ProjectSearchRequest): ProjectSearchRequest {
  return {
    ...params,
    pageable: {
      pageNumber: params.pageable?.pageNumber ?? 0,
      pageSize: params.pageable?.pageSize ?? 20,
      sort: params.pageable?.sort ?? { orders: [] }
    }
  };
}

class ProjectService {
  private http: HttpClient;

  constructor(http: HttpClient) {
    this.http = http;
  }

  async search(params: ProjectSearchRequest = {}): Promise<ProjectSearchResponse> {
    return this.http.requestAndValidate<ProjectSearchResponse>('/projects/search', {
      method: 'POST',
      body: normalizePageable(params)
    });
  }

  async getById(projectId: string, tenantId?: string): Promise<ProjectResponse> {
    return this.http.requestAndValidate<ProjectResponse>('/projects/by-id', {
      method: 'POST',
      body: { projectId, tenantId }
    });
  }

  async getByCode(code: string, tenantId?: string): Promise<ProjectResponse> {
    return this.http.requestAndValidate<ProjectResponse>('/projects/by-key', {
      method: 'POST',
      body: { code, tenantId }
    });
  }

  async register(data: ProjectRegisterRequest): Promise<ProjectRegisterResponse> {
    return this.http.requestAndValidate<ProjectRegisterResponse>('/projects/register', {
      method: 'POST',
      body: data
    });
  }

  async importProject(data: ProjectImportRequest): Promise<ProjectRegisterResponse> {
    return this.http.requestAndValidate<ProjectRegisterResponse>('/projects/import', {
      method: 'POST',
      body: data
    });
  }

  async update(data: ProjectUpdateRequest): Promise<BaseResponse> {
    return this.http.requestAndValidate<BaseResponse>('/projects', {
      method: 'PATCH',
      body: data
    });
  }

  async delete(projectId: string): Promise<BaseResponse> {
    return this.http.requestAndValidate<BaseResponse>('/projects', {
      method: 'DELETE',
      body: { projectId }
    });
  }
}

export { ProjectService };
